import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { SymbolNode } from "@/components/expression/SymbolNode";
import { UnitNode } from "@/components/expression/UnitNode";
import { Decorators } from "@/components/expression/utils/decorators/Decorators";
import { Group } from "@/components/expression/utils/groups/Group";
import * as Node from "@/components/expression/utils/layout";
import { NodeStyleProvider, useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { EditNodeInput } from "@/components/input/EditNodeInput";
import { canDuplicateAtCursor } from "@/components/input/utils/duplicateNodeAtCursor";
import { ButtonShortcut } from "@/components/sidebar/Button";
import { useTooltip } from "@/components/tooltip/Tooltip";
import { twMerge } from "@/utils/twMerge";

import { bracketsFromSlice } from "./inline-node/wrapping/buildRowTree";
import { useNodeThemeColors } from "./theme";
import { NodeAttachments, useAttachmentShift } from "./utils/NodeAttachments";

import type { ExpressionComponent } from "./types";
import type { GroupSlice } from "@/components/expression/inline-node/wrapping/types";
import type { Hotkey } from "@/utils/hotkeys";
import type { ReactNode } from "react";

type Props = {
  slice?: GroupSlice;
  children?: ReactNode;
  symbolWrapper?: (symbol: ReactNode) => ReactNode;
};

export const VariableNode: ExpressionComponent<"variable", Props> = observer(function VariableNode({
  node,
  className,
  slice = "whole",
  children,
  symbolWrapper,
}) {
  const t = useTranslations();
  const { doc } = node;
  const { renderContext, view } = useNodeStyle();

  let tooltipTitle = node.name;
  let hotkey: Hotkey | null = null;
  if (canDuplicateAtCursor(doc, node)) {
    tooltipTitle = t("node.tooltip.duplicateAtCursor");
    hotkey = "duplicate-at-cursor";
  }
  const { handlerProps, tooltip } = useTooltip({
    title: hotkey ? <ButtonShortcut text={tooltipTitle} hotkey={hotkey} /> : tooltipTitle,
    focusProps: {
      enabled: false,
    },
    hoverProps: {
      delay: {
        open: 1500,
      },
    },
  });
  const theme = useNodeThemeColors(node);
  const { shift, refs } = useAttachmentShift();

  const isEditing = doc.input.isEditing(node);
  const isTextView = view === "text";
  const shouldRenderParams = renderContext !== "objects-tree" && node.hasParams;
  const { showLeft, showRight } = bracketsFromSlice(slice);

  if (!showLeft && shouldRenderParams) {
    return (
      <Group
        node={node}
        parameters={node.parameters}
        openingSymbol={node.openingSymbol}
        closingSymbol={node.closingSymbol}
        bracketsClassName={twMerge(
          theme?.symbol &&
            `text-${theme.symbol.light} dark:text-${theme.symbol.dark} stroke-${theme.symbol.light} dark:stroke-${theme.symbol.dark}`,
        )}
        bgClassName="fill-function-body-bg"
        className={twMerge(!isTextView && "ml-0 -my-[1px]", className)}
        slice={slice}
      >
        {children}
      </Group>
    );
  }

  const symbolComp = (
    <EditNodeInput node={node}>
      <Decorators decorators={node.symbolNode?.decorators}>
        <NodeStyleProvider placeholderVisible="none">
          {node.symbolNode && (
            <SymbolNode
              node={node.symbolNode}
              className={twMerge(
                !isTextView && shouldRenderParams && "pl-0.5 pr-1",
                !isTextView && node.power && "pr-0.5",
              )}
              style={{ marginTop: shift }}
            />
          )}
        </NodeStyleProvider>
      </Decorators>
    </EditNodeInput>
  );

  const symbol = symbolWrapper?.(symbolComp) ?? symbolComp;

  return (
    <>
      <div
        className={twMerge(
          "flex",
          shouldRenderParams && "py-[1px]",
          !isTextView && shouldRenderParams && "mr-[4px]",
          className,
        )}
      >
        {node.unitNode?.placement === "prefix" && <UnitNode node={node.unitNode} />}
        <Node.Container
          data-testid="atomic-node"
          node={node}
          dimension="narrow"
          className={twMerge(
            !node.power && "items-stretch",
            !isTextView && shouldRenderParams && "rounded-r-1",
            isEditing && "px-0",
            !showRight && "pr-0",
            "transition duration-200",
            doc.editor.highlightedVariableSymbols.has(node.labeledValue) &&
              `ring-2 ring-${node.color}-400 dark:ring-${node.color}-300 scale-105 z-10`,
          )}
          {...handlerProps}
        >
          <div className="inline-flex">
            {symbol}
            {node.power && (
              <NodeAttachments
                {...refs}
                position="right"
                styleProviderValue={{ size: "xs" }}
                className={twMerge(!isTextView && "pr-0.5")}
                attachments={[
                  {
                    node: node.power.parameters[0],
                    position: "right-top",
                  },
                ]}
              />
            )}
          </div>
          {shouldRenderParams && (
            <Group
              node={node}
              parameters={node.parameters}
              openingSymbol={node.openingSymbol}
              closingSymbol={node.closingSymbol}
              bracketsClassName={twMerge(
                theme?.symbol &&
                  `text-${theme.symbol.light} dark:text-${theme.symbol.dark} stroke-${theme.symbol.light} dark:stroke-${theme.symbol.dark}`,
              )}
              bgClassName="fill-function-body-bg"
              className={twMerge(
                node.power && "self-stretch items-center",
                !isTextView && "ml-0 -my-[1px]",
              )}
              compensateBracketWidthRight={node.unitNode?.placement === "postfix" ? 0 : 4}
              slice={slice}
            >
              {children}
            </Group>
          )}
        </Node.Container>
        {node.unitNode?.placement === "postfix" && <UnitNode node={node.unitNode} />}
      </div>
      {tooltip}
    </>
  );
});
